/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright (c) 2026 Steven Warehouse Project. All Rights Reserved.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  AuthError,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { api } from '../lib/api';

// In local dev, use popup (works despite cross-origin, just has timeout UX issue)
// In production, use redirect (works perfectly with auth proxy on same domain)
const USE_REDIRECT_FLOW = !import.meta.env.DEV;

interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  role: 'admin' | 'user';
}

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  accessDenied: string | null;
  clearAccessDenied: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectChecked, setRedirectChecked] = useState(false);
  const [accessDenied, setAccessDenied] = useState<string | null>(null);

  // ─── Handle redirect result on mount (PRODUCTION ONLY) ──
  // In local dev, we use popup so no redirect result to check
  useEffect(() => {
    if (!USE_REDIRECT_FLOW) {
      // Local dev uses popup, skip redirect check
      setRedirectChecked(true);
      return;
    }

    let cancelled = false;
    
    getRedirectResult(auth)
      .then((result) => {
        if (cancelled) return;
        if (result) {
          console.log('Redirect sign-in completed for', result.user.email);
        }
        setRedirectChecked(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Redirect sign-in error:', err);
        setRedirectChecked(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Don't process auth state until we've checked for redirect result
    if (!redirectChecked) return;

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      
      if (fbUser) {
        try {
          // Get ID token and verify with backend
          const idToken = await fbUser.getIdToken();
          const response = await api.post<{ user: User }>('/auth/verify', { idToken });
          setAccessDenied(null);
          setUser(response.user);
        } catch (error: unknown) {
          // Check for 403 Access Denied (not in the approved allowlist)
          const apiError = error as Error & { status?: number };
          if (apiError?.status === 403) {
            const msg =
              apiError.message ||
              'Access denied. You have not been granted access to this platform. Please contact your administrator.';
            setAccessDenied(msg);
            // Sign out of Firebase so the user isn't stuck in a 403 loop on every reload
            await signOut(auth);
          } else {
            console.error('Failed to verify user:', error);
          }
          setUser(null);
        }
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [redirectChecked]);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      
      if (USE_REDIRECT_FLOW) {
        // Production: Use redirect flow (works with auth proxy on same domain)
        await signInWithRedirect(auth, googleProvider);
        // Page navigates away, never reaches this line
      } else {
        // Local dev: Use popup flow (works despite cross-origin)
        await signInWithPopup(auth, googleProvider);
        // Success - onAuthStateChanged will fire
        setLoading(false);
      }
    } catch (error) {
      const authError = error as AuthError;
      
      // Handle popup-specific errors gracefully
      if (authError.code === 'auth/popup-closed-by-user') {
        // User closed popup - not really an error, just cancelled
        console.log('User cancelled sign-in');
      } else if (authError.code === 'auth/popup-blocked') {
        // Popup was blocked - show user-friendly message
        console.error('Popup blocked by browser');
      } else {
        console.error('Sign-in error:', authError.code);
      }
      
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const clearAccessDenied = () => setAccessDenied(null);

  const value = {
    user,
    firebaseUser,
    loading,
    signInWithGoogle,
    logout,
    isAdmin: user?.role === 'admin',
    accessDenied,
    clearAccessDenied,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
